import { getToken } from '../context/AuthContext';

const getApiUrl = () => (
  process.env.REACT_APP_API_URL || 'http://localhost:8080'
);

const getWsBaseUrl = () => {
  const apiUrl = getApiUrl();
  const baseUrl = apiUrl.replace(/\/$/, '');
  if (baseUrl.startsWith('https://')) {
    return baseUrl.replace('https://', 'wss://');
  }
  return baseUrl.replace('http://', 'ws://');
};

const getDirectWsBaseUrl = () => {
  if (process.env.REACT_APP_SEAT_WS_DIRECT_URL) {
    return process.env.REACT_APP_SEAT_WS_DIRECT_URL.replace(/\/$/, '');
  }
  const apiUrl = getApiUrl();
  if (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')) {
    return 'ws://localhost:8093';
  }
  return null;
};

const buildWsUrl = (baseUrl, showId, token) => (
  `${baseUrl}/ws/seats?showId=${encodeURIComponent(showId)}&token=${encodeURIComponent(token)}`
);

const connections = new Map();
const CLOSE_DELAY_MS = 500;

const getBaseUrl = () => {
  const directBase = getDirectWsBaseUrl();
  if (process.env.REACT_APP_SEAT_WS_FORCE_GATEWAY) {
    return getWsBaseUrl();
  }
  return directBase || getWsBaseUrl();
};

const createConnection = (showId, token) => {
  const baseUrl = getBaseUrl();
  const wsUrl = buildWsUrl(baseUrl, showId, token);
  const connection = {
    socket: new WebSocket(wsUrl),
    subscribers: new Set(),
    closeTimer: null,
    manualClose: false,
  };

  connection.socket.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data);
      connection.subscribers.forEach((subscriber) => {
        if (subscriber.onMessage) {
          subscriber.onMessage(payload);
        }
      });
    } catch (error) {
      connection.subscribers.forEach((subscriber) => {
        if (subscriber.onError) {
          subscriber.onError('Invalid websocket payload');
        }
      });
    }
  };

  connection.socket.onerror = () => {
    connection.subscribers.forEach((subscriber) => {
      if (subscriber.onError) {
        subscriber.onError('WebSocket error');
      }
    });
  };

  connection.socket.onclose = () => {
    if (!connection.manualClose) {
      connection.subscribers.forEach((subscriber) => {
        if (subscriber.onError) {
          subscriber.onError('WebSocket closed');
        }
      });
    }
  };

  return connection;
};

const getOrCreateConnection = (showId, token) => {
  const existing = connections.get(showId);
  if (existing && existing.socket.readyState !== WebSocket.CLOSED) {
    return existing;
  }
  const connection = createConnection(showId, token);
  connections.set(showId, connection);
  return connection;
};

const scheduleCloseIfIdle = (showId, connection) => {
  if (connection.closeTimer) {
    clearTimeout(connection.closeTimer);
  }
  connection.closeTimer = setTimeout(() => {
    if (connection.subscribers.size === 0) {
      connection.manualClose = true;
      connection.socket.close();
      connections.delete(showId);
    }
  }, CLOSE_DELAY_MS);
};

export const connectSeatWebSocket = (showId, onMessage, onError) => {
  const token = getToken();
  if (!token) {
    if (onError) onError('Missing auth token');
    return null;
  }

  const connection = getOrCreateConnection(showId, token);
  const subscriber = { onMessage, onError };
  connection.subscribers.add(subscriber);
  if (connection.closeTimer) {
    clearTimeout(connection.closeTimer);
  }

  return {
    close: () => {
      connection.subscribers.delete(subscriber);
      scheduleCloseIfIdle(showId, connection);
    },
  };
};
